// ═══ 업무별 기록 v5 — 처리 기록 (누가·언제·무엇을·메모, DESIGN_V5 §5·§6.6) ═══
// 장부 반영·취소·확인 항목 결정·겹침 선택을 시간순(최근 먼저)으로 보여 준다. 50건씩.
// 서버 계약: GET /work-records/events?jobKind=&offset=&limit=50 (limit ≤200) → {success, total, events:[{id, kind, cardNo, gapNo, actorName, actorStaff, actorRole, memo, summary, at}]}
//
// props: jobKind · reloadKey
import React, { useState, useEffect } from 'react';
import { formatArrivedAtKst } from '../utils/dateKst';
import { fmt, num, signed, getJson, th, td, errBoxSt, CHOICE_LABEL } from './RecordsPreviewModal';

var PAGE = 50;
var EVENT_LABEL = { post: '장부에 반영', undo: '반영 취소', decide: '확인 항목 결정', decide_change: '결정 바꿈', decide_undo: '결정 취소', realign: '다시 맞춤', overlap: 'ERP 겹침 정함' };
var OVERLAP_LABEL = { exclude: '엑셀 건 빼기', separate: '별건으로 넣기', clear: '선택 해제' };
var lineSum = function (lines) { return (Array.isArray(lines) ? lines : []).reduce(function (t, l) { return t + num(l.amount); }, 0); };
// summary(JSON) — 종류별 모양 (API_V5_CHANGES §8): post {posted:{items,charges,payments,adjusts}, net, byUnit} ·
//   undo {removed, reversed, detached:[], decisionsReversed} · decide/decide_change {choice, lines, replaced, atPost?} ·
//   realign {lines} · decide_undo {choice, run} · overlap {choice, recordIds}
var summaryText = function (kind, s) {
  if (!s || typeof s !== 'object') return '';
  var out = [];
  if (kind === 'post') {
    var p = s.posted || {};
    out.push('건 ' + fmt(p.items), '입금 ' + fmt(p.payments));
    if (num(p.adjusts)) out.push('조정 ' + fmt(p.adjusts));
    out.push('미수 ' + signed(s.net) + '원');
  } else if (kind === 'undo') {
    out.push('지운 건 ' + fmt(s.removed), '되돌린 줄 ' + fmt(s.reversed));
    if ((s.detached || []).length) out.push('남긴 고친 기록 ' + fmt(s.detached.length));
    if (num(s.decisionsReversed)) out.push('함께 취소한 결정 ' + fmt(s.decisionsReversed));
  } else if (kind === 'decide' || kind === 'decide_change' || kind === 'decide_undo' || kind === 'realign') {
    if (s.choice) out.push(CHOICE_LABEL[s.choice] || s.choice);
    if ((s.lines || []).length) out.push('정리 ' + signed(lineSum(s.lines)) + '원');
    if (s.atPost) out.push('반영과 함께');
  } else if (kind === 'overlap') {
    out.push((OVERLAP_LABEL[s.choice] || s.choice) + ' ' + fmt((s.recordIds || []).length) + '건');
  }
  return out.join(' · ');
};

export default function RecordsHistory(props) {
  var jobKind = props.jobKind;
  var [offset, setOffset] = useState(0);
  var [data, setData] = useState(null);
  var [err, setErr] = useState('');

  useEffect(function () { setOffset(0); }, [jobKind]);
  useEffect(function () {
    var alive = true;
    getJson('/work-records/events?jobKind=' + encodeURIComponent(jobKind) + '&offset=' + offset + '&limit=' + PAGE).then(function (res) {
      if (!alive) return;
      var d = res.data;
      if (!d.success || !Array.isArray(d.events)) { setErr(d.error || '조회 실패'); setData({ total: 0, events: [] }); return; }
      setErr(''); setData({ total: num(d.total), events: d.events });
    }).catch(function () { if (alive) { setErr('조회 실패'); setData({ total: 0, events: [] }); } });
    return function () { alive = false; };
  }, [jobKind, offset, props.reloadKey]);

  var events = (data && data.events) || [];
  var total = (data && data.total) || 0;
  return (<div>
    <p style={{ margin: '0 0 10px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>장부 반영·취소와 확인 항목 결정을 누가 언제 했는지 보여 줍니다.</p>
    {!!err && <div style={errBoxSt}>{err}</div>}
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', background: '#fff', border: '1px solid #e2e8f0' }}>
        <thead><tr>{['언제', '한 일', '카드·항목', '한 사람', '메모', '내용'].map(function (h) { return <th key={h} style={th}>{h}</th>; })}</tr></thead>
        <tbody>{events.map(function (e) {
          return (<tr key={e.id} data-testid="history-row">
            <td style={td}>{formatArrivedAtKst(e.at) || '-'}</td>
            <td style={Object.assign({}, td, { fontWeight: 700, color: '#1B3A5C' })}>{EVENT_LABEL[e.kind] || e.kind}</td>
            <td style={td}>{[e.cardNo, e.gapNo].filter(Boolean).join(' · ') || '-'}</td>
            <td style={td}>{e.actorName || '-'}{e.actorStaff ? ' (직원)' : ''}{e.actorRole && e.actorRole !== 'cancel_agency' ? ' (관리자)' : ''}</td>
            <td style={Object.assign({}, td, { whiteSpace: 'normal' })}>{e.memo || '-'}</td>
            <td style={Object.assign({}, td, { whiteSpace: 'normal', color: '#475569' })}>{summaryText(e.kind, e.summary) || '-'}</td>
          </tr>);
        })}
        {!events.length && <tr><td colSpan={6} style={Object.assign({}, td, { padding: 20, textAlign: 'center', color: '#94a3b8' })}>{data ? '아직 기록이 없습니다' : '조회 중…'}</td></tr>}
        </tbody>
      </table>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13.5, color: '#64748b' }}>
      <span>총 {fmt(total)}건{total > 0 ? ' · ' + fmt(offset + 1) + '–' + fmt(Math.min(offset + PAGE, total)) : ''}</span>
      <button className="btn btn-outline btn-sm" disabled={offset === 0} onClick={function () { setOffset(Math.max(0, offset - PAGE)); }}>이전</button>
      <button className="btn btn-outline btn-sm" disabled={offset + PAGE >= total} onClick={function () { setOffset(offset + PAGE); }}>다음</button>
    </div>
  </div>);
}
